
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Components
import Login from './pages/Auth/Login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import BecomeAVendor from './pages/BecomeAVendor/BecomeAVendor';
import OnboardingStep1 from './pages/Onboarding/OnboardingStep1'; // Password setup
import OnboardingStep2 from './pages/Onboarding/OnboardingStep2'; // Company profile
import OnboardingStep3 from './pages/Onboarding/OnboardingStep3'; // Upload image
import OnboardingStep4 from './pages/Onboarding/OnboardingStep4'; // Business info
import OnboardingStep5 from './pages/Onboarding/OnboardingStep5'; // Services (updated with API)
import OnboardingStep6 from './pages/Onboarding/OnboardingStep6'; // Occasions setup
import CompanyProfile from './pages/CompanyProfile/CompanyProfile';
import Products from './pages/Products/Products';
import ProductDetail from './pages/ProductDetail/ProductDetail';
import Dashboard from './pages/Dashboard/Dashboard';
import Orders from './pages/Orders/Orders';
import OrderDetail from './pages/OrderDetail/OrderDetail';
import Transactions from './pages/Transactions/Transactions';
import Reviews from './pages/Reviews/Reviews';
import Questions from './pages/Questions/Questions';
import Notifications from './pages/Notifications/Notifications';
import Settings from './pages/Settings/Settings';
import VendorPortalLayout from './components/Layout/VendorPortalLayout';

// Contexts
import { VendorAuthProvider } from './contexts/VendorAuthContext';
import { VendorProfileProvider } from './contexts/VendorProfileContext';
import ProtectedRoute from './components/ProtectedRoute';

// Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

// Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <VendorAuthProvider>
          <VendorProfileProvider>
            <Router>
              <Box sx={{ display: 'flex' }}>
                  <Routes>
                {/* Public Routes */}
                <Route path="/login" element={
                  <ProtectedRoute requireAuth={false}>
                    <Login />
                  </ProtectedRoute>
                } />
                <Route path="/forgot-password" element={
                  <ProtectedRoute requireAuth={false}>
                    <ForgotPassword />
                  </ProtectedRoute>
                } />
                <Route path="/reset-password" element={
                  <ProtectedRoute requireAuth={false}>
                    <ResetPassword />
                  </ProtectedRoute>
                } />
                <Route path="/become-vendor" element={<BecomeAVendor />} />
                
                {/* Special Onboarding Step 1 - Token-based authentication */}
                <Route path="/onboarding/step1" element={<OnboardingStep1 />} />
                
                {/* Protected Onboarding Routes */}
                <Route path="/onboarding/step2" element={
                  <ProtectedRoute>
                    <OnboardingStep2 />
                  </ProtectedRoute>
                } />
                <Route path="/onboarding/step3" element={
                  <ProtectedRoute>
                    <OnboardingStep3 />
                  </ProtectedRoute>
                } />
                <Route path="/onboarding/step4" element={
                  <ProtectedRoute>
                    <OnboardingStep4 />
                  </ProtectedRoute>
                } />
                <Route path="/onboarding/step5" element={
                  <ProtectedRoute>
                    <OnboardingStep5 />
                  </ProtectedRoute>
                } />
                <Route path="/onboarding/step6" element={
                  <ProtectedRoute>
                    <OnboardingStep6 />
                  </ProtectedRoute>
                } />
                
                {/* Default redirect for root */}
                <Route path="/" element={<Navigate to="/company-profile" replace />} />

                {/* Protected Vendor Portal */}
                <Route
                  element={
                    <ProtectedRoute>
                      <VendorPortalLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/company-profile" element={<CompanyProfile />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/products/:productId" element={<ProductDetail />} />
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/orders/:id" element={<OrderDetail />} />
                  <Route 
                    path="/transactions" 
                    element={<Transactions />} 
                  />
                  <Route 
                    path="/reviews" 
                    element={<Reviews />} 
                  />
                  <Route 
                    path="/questions" 
                    element={<Questions />} 
                  />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/settings" element={<Settings />} />
                </Route>

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
            </Box>
          </Router>
          </VendorProfileProvider>
        </VendorAuthProvider>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
